/** Frozen inc-v2-20261018.1 wire shapes. Explicit nulls are preserved; no balance inference. */
export const CONTRACT_REVISION = 'inc-v2-20261018.1' as const
export const LEGACY_CONTRACT_REVISION = 'inc-v2-20261019.legacy1' as const
export interface Transition { state: 'not_migrated' | 'migrating' | 'ready' | 'blocked'; effective_at: number | null; reason_code: string | null; retryable: boolean }
export interface Achievement { grant_id: string; source: 'historical_backfill' | 'live'; qualified_at: number | null; granted_at: number; reward_disposition: 'historical_no_reward' | 'normal' | 'non_rewarding' }
export interface CommemorativeBadge { key: string; name: string; description?: string; icon_url?: string | null; icon_key: string; earned: boolean; achievement: Achievement }
export type SectionKey = 'badge' | 'creator_weekly' | 'newcomer'
export type Metric = 'public_songs' | 'plays' | 'likes' | 'remixes'
export type AnalyticsRangeKey = 'last_7_days' | 'last_30_days'
export interface Envelope {
  schema_version: 2
  subject_id: string
  scope_id: string
  state_revision: number
  server_time: number
  as_of: number
  reward_engine: 'legacy' | 'v2'
}
export interface Page {
  next_cursor: string | null
  has_more: boolean
}
export interface Window {
  period_id: string | null
  starts_at: number | null
  ends_at: number | null
  reset_at: number | null
  timezone: string
}
export interface Totals {
  granted_points: number
  pending_points: number
  reversed_points: number
  cap_points: number | null
  cap_used_points: number
  remaining_points: number | null
}
export interface Notice {
  code: string
  text: string
  ends_at: number | null
}
export interface Presentation {
  entry_visible: boolean
  entry_state: string
  display_until: number | null
  actionable_remaining_points: number | null
  next_deadline_at: number | null
}
export interface QuotaPool {
  key: string
  cap_points: number
  used_points: number
  remaining_points: number
  eligible: boolean
  ends_at: number
}
export interface Section {
  key: SectionKey
  title: string
  description: string
  status: string
  policy_revision_id: string | null
  window: Window
  totals: Totals
  task_count: number
  notice: Notice | null
  quota_pools: QuotaPool[] | null
  presentation: Presentation
}
export interface Badge {
  key: string
  name: string
  description: string
  icon_url: string | null
  icon_key: string
  difficulty: string
  threshold: number
  progress_count: number | null
  progress_evidence?: { coverage: string; value: number | null; since: number | null }
  achievement?: Achievement | null
  reward_reason_code?: string
  earned: boolean
  earned_at: number | null
  reward_points: number | null
  reward_receipt_id: string | null
  reward_status: string | null
  enabled: boolean
  sort: number
  reward_enabled: boolean
  reward_policy_revision_id: string | null
  reward_display_state: string
  reward_record: RewardRecord | null
  locked_reward_points: number | null
}
export interface FirstCompletion {
  source: string
  eligible: boolean
  task_id: string | null
  completed_at: number | null
  seen_at: number | null
  should_present: boolean
}
export interface Overview extends Envelope {
  contract_revision?: string
  transition?: Transition
  commemorative_badges?: CommemorativeBadge[]
  sections: Section[]
  badges: Badge[]
  first_completion: FirstCompletion
  recent_rewards: RewardRecord[]
}
export interface EligibilityWindow {
  starts_at: number
  ends_at: number
}
export interface Action {
  type: string
  content_id: string | null
}
export interface Limits {
  cap_points: number | null
  cap_used_points: number
  remaining_points: number | null
  repeat_used: number
  repeat_limit: number | null
}
export interface Progress {
  content_id: string | null
  content: ProgressContent | null
  count: number
  target: number
  remaining: number
  next_ordinal: number
  completed_units: number
  pending_units: number
  is_complete: boolean
}
export interface ProgressContent {
  id: string
  title: string | null
  cover_url: string | null
  status: string
}
export interface Task {
  id: string
  reward_type: SectionKey
  rule_id: string
  policy_revision_id: string
  title: string
  description: string
  icon_url: string | null
  metric: string
  target: number
  unit: string
  count_scope: string
  period_scope: string
  repeat_limit: number | null
  reward_points: number
  status: string
  reason_code: string | null
  eligibility_window: EligibilityWindow | null
  next_action: Action
  limits: Limits
  progress_by_content: Progress[]
  progress_page: Page
  recent_receipts: RewardRecord[]
}
export interface Tasks extends Envelope {
  section: SectionKey
  period_id: string
  list: Task[]
  page: Page
}
export interface Source {
  category: string
  source_code: string
  rule_id: string | null
  badge_key: string | null
  content_id: string | null
  content_title: string | null
  period_id: string | null
  period_label: string | null
  title: string
  subtitle: string | null
  message_revision_id: string | null
  legacy: boolean
}
export interface BalanceEntry {
  id: string
  direction: string
  points: number
  occurred_at: number
  related_entry_id: string | null
}
export interface RewardRecord {
  id: string
  reward_type: SectionKey
  rule_id: string
  policy_revision_id: string
  correlation_id: string
  source_event_id: string
  content_id: string | null
  badge_key: string | null
  period_id: string
  status: string
  points: number
  granted_points: number
  reversed_points: number
  qualified_at: number
  created_at: number
  due_at: number | null
  granted_at: number | null
  reason_code: string | null
  overdue: boolean
  notification_policy: string
  adjustment_pending: boolean
  written_off_points: number
  legacy: boolean
  source: Source | null
  balance_entries: BalanceEntry[] | null
}
export interface RecordSummary {
  scope: string
  granted_points: number
  pending_points: number
  reversed_points: number
  record_count: number
  as_of: number
}
export interface Records extends Envelope {
  list: RewardRecord[]
  page: Page
  summary: RecordSummary | null
}
export interface Notification {
  id: string
  kind: string
  correlation_id: string
  receipt_ids: string[]
  badge_keys: string[]
  created_at: number
  seen_at: number | null
}
export interface Notifications extends Envelope {
  list: Notification[]
  page: Page
}
export interface SeenResult {
  newly_seen_ids: string[]
}
export interface AnalyticsRange {
  key: string
  start_at: number
  end_at: number
}
export interface AnalyticsEnvelope {
  schema_version: 2
  subject_id: string
  server_time: number
  as_of: number
  status: string
  projection_revision: string
  timezone: string
  range: AnalyticsRange
  supported_ranges: string[]
  reward_engine: 'legacy' | 'v2'
}
export interface MetricValue {
  value: number | null
  scope: string
  status: string
  as_of: number
  coverage_start_at: number | null
  explanation_code: string | null
}
export interface Metrics {
  public_songs: MetricValue
  plays: MetricValue
  likes: MetricValue
  remixes: MetricValue
}
export interface MetricDefinition {
  key: string
  title: string
  unit: string
  summary_semantics: string
  series_semantics: string
  qualification_text: string
  range_supported: boolean
}
export interface SeriesPoint {
  bucket_start: number
  bucket_end: number
  value: number | null
  status: string
}
export interface Series {
  metric: string
  status: string
  as_of: number
  available_from: number | null
  points: SeriesPoint[]
  explanation_code: string | null
}
export interface AnalyticsOverview extends AnalyticsEnvelope {
  summary: Metrics
  metric_definitions: MetricDefinition[]
  series: Series
  content_snapshot_id: string
}
export interface ContentRow {
  content_id: string
  title: string
  cover_url: string | null
  public_since: number | null
  metrics: Metrics
}
export interface AnalyticsContents extends AnalyticsEnvelope {
  list: ContentRow[]
  page: Page
  scope_label: string
}
export interface InvitationCode {
  code: string
  share_ref: string
  share_url: string
  status: string
  created_at: number
}
export interface Inviter {
  display_name: string
  avatar_url: string | null
}
export interface Attribution {
  source_kind: string
  inviter: Inviter
  binding_effective_at: number
}
export interface Redemption {
  state: string
  requested_at: number | null
  accepted_at: number | null
  receipt_id: string | null
  reason_code: string | null
}
export interface FirstCreation {
  state: string
  first_success_at: number | null
  qualified_at: number | null
}
export interface FillWindow {
  starts_at: number
  ends_at: number
  eligible: boolean
  reason_code: string | null
}
export interface InvitationOverview extends Envelope {
  my_code: InvitationCode | null
  attribution: Attribution | null
  redemption: Redemption
  first_creation: FirstCreation
  fill_window: FillWindow
  quota_pools: QuotaPool[] | null
}
export interface InvitationAttributionResult extends Envelope {
  attribution: Attribution | null
  relation_result: string
}
export interface EnsureInvitationResult extends Envelope {
  my_code: InvitationCode | null
}
export interface RedeemResult extends Envelope {
  attribution: Attribution | null
  redemption: Redemption
  relation_result: string | null
  submitted_owner_matches: boolean | null
  reward_record: RewardRecord | null
  notification_ids: string[]
  reward_state_revision: number
}
export interface InvitationBenefits {
  enabled: boolean
  title: string
  description: string
  welcome_points: number | null
  invite_code_points: number | null
  fill_window_seconds: number | null
}
export interface InvitationResolve {
  inviter_display: Inviter | null
  code: string | null
  benefit_copy: InvitationBenefits
  web_url: string | null
  ios_open_url: string | null
  ios_store_url: string | null
  status: string
}
export interface InvitationOpen {
  accepted: boolean
  invite_open_token: string | null
  expires_at: number | null
}

// All response properties are required; arrays and nullable fields stay distinct.
const shapes: Record<string, Record<string, string>> = {
  Envelope: {
    schema_version: 'int',
    subject_id: 'string',
    scope_id: 'string',
    state_revision: 'int64',
    server_time: 'int64',
    as_of: 'int64',
    reward_engine: 'string',
  },
  Page: {
    next_cursor: '*string',
    has_more: 'bool',
  },
  Window: {
    period_id: '*string',
    starts_at: '*int64',
    ends_at: '*int64',
    reset_at: '*int64',
    timezone: 'string',
  },
  Totals: {
    granted_points: 'int64',
    pending_points: 'int64',
    reversed_points: 'int64',
    cap_points: '*int64',
    cap_used_points: 'int64',
    remaining_points: '*int64',
  },
  Notice: {
    code: 'string',
    text: 'string',
    ends_at: '*int64',
  },
  Presentation: {
    entry_visible: 'bool',
    entry_state: 'string',
    display_until: '*int64',
    actionable_remaining_points: '*int64',
    next_deadline_at: '*int64',
  },
  QuotaPool: {
    key: 'string',
    cap_points: 'int64',
    used_points: 'int64',
    remaining_points: 'int64',
    eligible: 'bool',
    ends_at: 'int64',
  },
  Section: {
    key: 'SectionKey',
    title: 'string',
    description: 'string',
    status: 'string',
    policy_revision_id: '*string',
    window: 'Window',
    totals: 'Totals',
    task_count: 'int64',
    notice: '*Notice',
    quota_pools: '*[]QuotaPool',
    presentation: 'Presentation',
  },
  Badge: {
    key: 'string',
    name: 'string',
    description: 'string',
    icon_url: '*string',
    icon_key: 'string',
    difficulty: 'string',
    threshold: 'int64',
    progress_count: 'int64',
    earned: 'bool',
    earned_at: '*int64',
    reward_points: '*int64',
    reward_receipt_id: '*string',
    reward_status: '*string',
    enabled: 'bool',
    sort: 'int64',
    reward_enabled: 'bool',
    reward_policy_revision_id: '*string',
    reward_display_state: 'string',
    reward_record: '*RewardRecord',
    locked_reward_points: '*int64',
  },
  FirstCompletion: {
    source: 'string',
    eligible: 'bool',
    task_id: '*string',
    completed_at: '*int64',
    seen_at: '*int64',
    should_present: 'bool',
  },
  Overview: {
    schema_version: 'int',
    subject_id: 'string',
    scope_id: 'string',
    state_revision: 'int64',
    server_time: 'int64',
    as_of: 'int64',
    reward_engine: 'string',
    sections: '[]Section',
    badges: '[]Badge',
    first_completion: 'FirstCompletion',
    recent_rewards: '[]RewardRecord',
  },
  EligibilityWindow: {
    starts_at: 'int64',
    ends_at: 'int64',
  },
  Action: {
    type: 'string',
    content_id: '*string',
  },
  Limits: {
    cap_points: '*int64',
    cap_used_points: 'int64',
    remaining_points: '*int64',
    repeat_used: 'int64',
    repeat_limit: '*int64',
  },
  Progress: {
    content_id: '*string',
    content: '*ProgressContent',
    count: 'int64',
    target: 'int64',
    remaining: 'int64',
    next_ordinal: 'int64',
    completed_units: 'int64',
    pending_units: 'int64',
    is_complete: 'bool',
  },
  ProgressContent: {
    id: 'string',
    title: '*string',
    cover_url: '*string',
    status: 'string',
  },
  Task: {
    id: 'string',
    reward_type: 'SectionKey',
    rule_id: 'string',
    policy_revision_id: 'string',
    title: 'string',
    description: 'string',
    icon_url: '*string',
    metric: 'string',
    target: 'int64',
    unit: 'string',
    count_scope: 'string',
    period_scope: 'string',
    repeat_limit: '*int64',
    reward_points: 'int64',
    status: 'string',
    reason_code: '*string',
    eligibility_window: '*EligibilityWindow',
    next_action: 'Action',
    limits: 'Limits',
    progress_by_content: '[]Progress',
    progress_page: 'Page',
    recent_receipts: '[]RewardRecord',
  },
  Tasks: {
    schema_version: 'int',
    subject_id: 'string',
    scope_id: 'string',
    state_revision: 'int64',
    server_time: 'int64',
    as_of: 'int64',
    reward_engine: 'string',
    section: 'SectionKey',
    period_id: 'string',
    list: '[]Task',
    page: 'Page',
  },
  Source: {
    category: 'string',
    source_code: 'string',
    rule_id: '*string',
    badge_key: '*string',
    content_id: '*string',
    content_title: '*string',
    period_id: '*string',
    period_label: '*string',
    title: 'string',
    subtitle: '*string',
    message_revision_id: '*string',
    legacy: 'bool',
  },
  BalanceEntry: {
    id: 'string',
    direction: 'string',
    points: 'int64',
    occurred_at: 'int64',
    related_entry_id: '*string',
  },
  RewardRecord: {
    id: 'string',
    reward_type: 'SectionKey',
    rule_id: 'string',
    policy_revision_id: 'string',
    correlation_id: 'string',
    source_event_id: 'string',
    content_id: '*string',
    badge_key: '*string',
    period_id: 'string',
    status: 'string',
    points: 'int64',
    granted_points: 'int64',
    reversed_points: 'int64',
    qualified_at: 'int64',
    created_at: 'int64',
    due_at: '*int64',
    granted_at: '*int64',
    reason_code: '*string',
    overdue: 'bool',
    notification_policy: 'string',
    adjustment_pending: 'bool',
    written_off_points: 'int64',
    legacy: 'bool',
    source: '*Source',
    balance_entries: '*[]BalanceEntry',
  },
  RecordSummary: {
    scope: 'string',
    granted_points: 'int64',
    pending_points: 'int64',
    reversed_points: 'int64',
    record_count: 'int64',
    as_of: 'int64',
  },
  Records: {
    schema_version: 'int',
    subject_id: 'string',
    scope_id: 'string',
    state_revision: 'int64',
    server_time: 'int64',
    as_of: 'int64',
    reward_engine: 'string',
    list: '[]RewardRecord',
    page: 'Page',
    summary: '*RecordSummary',
  },
  Notification: {
    id: 'string',
    kind: 'string',
    correlation_id: 'string',
    receipt_ids: '[]string',
    badge_keys: '[]string',
    created_at: 'int64',
    seen_at: '*int64',
  },
  Notifications: {
    schema_version: 'int',
    subject_id: 'string',
    scope_id: 'string',
    state_revision: 'int64',
    server_time: 'int64',
    as_of: 'int64',
    reward_engine: 'string',
    list: '[]Notification',
    page: 'Page',
  },
  SeenResult: {
    newly_seen_ids: '[]string',
  },
  AnalyticsRange: {
    key: 'string',
    start_at: 'int64',
    end_at: 'int64',
  },
  AnalyticsEnvelope: {
    schema_version: 'int',
    subject_id: 'string',
    server_time: 'int64',
    as_of: 'int64',
    status: 'string',
    projection_revision: 'string',
    timezone: 'string',
    range: 'AnalyticsRange',
    supported_ranges: '[]string',
    reward_engine: 'string',
  },
  MetricValue: {
    value: '*int64',
    scope: 'string',
    status: 'string',
    as_of: 'int64',
    coverage_start_at: '*int64',
    explanation_code: '*string',
  },
  Metrics: {
    public_songs: 'MetricValue',
    plays: 'MetricValue',
    likes: 'MetricValue',
    remixes: 'MetricValue',
  },
  MetricDefinition: {
    key: 'string',
    title: 'string',
    unit: 'string',
    summary_semantics: 'string',
    series_semantics: 'string',
    qualification_text: 'string',
    range_supported: 'bool',
  },
  SeriesPoint: {
    bucket_start: 'int64',
    bucket_end: 'int64',
    value: '*int64',
    status: 'string',
  },
  Series: {
    metric: 'string',
    status: 'string',
    as_of: 'int64',
    available_from: '*int64',
    points: '[]SeriesPoint',
    explanation_code: '*string',
  },
  AnalyticsOverview: {
    schema_version: 'int',
    subject_id: 'string',
    server_time: 'int64',
    as_of: 'int64',
    status: 'string',
    projection_revision: 'string',
    timezone: 'string',
    range: 'AnalyticsRange',
    supported_ranges: '[]string',
    reward_engine: 'string',
    summary: 'Metrics',
    metric_definitions: '[]MetricDefinition',
    series: 'Series',
    content_snapshot_id: 'string',
  },
  ContentRow: {
    content_id: 'string',
    title: 'string',
    cover_url: '*string',
    public_since: '*int64',
    metrics: 'Metrics',
  },
  AnalyticsContents: {
    schema_version: 'int',
    subject_id: 'string',
    server_time: 'int64',
    as_of: 'int64',
    status: 'string',
    projection_revision: 'string',
    timezone: 'string',
    range: 'AnalyticsRange',
    supported_ranges: '[]string',
    reward_engine: 'string',
    list: '[]ContentRow',
    page: 'Page',
    scope_label: 'string',
  },
  InvitationCode: {
    code: 'string',
    share_ref: 'string',
    share_url: 'string',
    status: 'string',
    created_at: 'int64',
  },
  Inviter: {
    display_name: 'string',
    avatar_url: '*string',
  },
  Attribution: {
    source_kind: 'string',
    inviter: 'Inviter',
    binding_effective_at: 'int64',
  },
  Redemption: {
    state: 'string',
    requested_at: '*int64',
    accepted_at: '*int64',
    receipt_id: '*string',
    reason_code: '*string',
  },
  FirstCreation: {
    state: 'string',
    first_success_at: '*int64',
    qualified_at: '*int64',
  },
  FillWindow: {
    starts_at: 'int64',
    ends_at: 'int64',
    eligible: 'bool',
    reason_code: '*string',
  },
  InvitationOverview: {
    schema_version: 'int',
    subject_id: 'string',
    scope_id: 'string',
    state_revision: 'int64',
    server_time: 'int64',
    as_of: 'int64',
    reward_engine: 'string',
    my_code: '*InvitationCode',
    attribution: '*Attribution',
    redemption: 'Redemption',
    first_creation: 'FirstCreation',
    fill_window: 'FillWindow',
    quota_pools: '*[]QuotaPool',
  },
  InvitationAttributionResult: {
    schema_version: 'int', subject_id: 'string', scope_id: 'string', state_revision: 'int64',
    server_time: 'int64', as_of: 'int64', reward_engine: 'string', attribution: '*Attribution', relation_result: 'string',
  },
  EnsureInvitationResult: {
    schema_version: 'int', subject_id: 'string', scope_id: 'string', state_revision: 'int64',
    server_time: 'int64', as_of: 'int64', reward_engine: 'string', my_code: '*InvitationCode',
  },
  RedeemResult: {
    schema_version: 'int',
    subject_id: 'string',
    scope_id: 'string',
    state_revision: 'int64',
    server_time: 'int64',
    as_of: 'int64',
    reward_engine: 'string',
    attribution: '*Attribution',
    redemption: 'Redemption',
    relation_result: '*string',
    submitted_owner_matches: '*bool',
    reward_record: '*RewardRecord',
    notification_ids: '[]string',
    reward_state_revision: 'int64',
  },
  InvitationBenefits: {
    enabled: 'bool',
    title: 'string',
    description: 'string',
    welcome_points: '*int64',
    invite_code_points: '*int64',
    fill_window_seconds: '*int64',
  },
  InvitationResolve: {
    inviter_display: '*Inviter',
    code: '*string',
    benefit_copy: 'InvitationBenefits',
    web_url: '*string',
    ios_open_url: '*string',
    ios_store_url: '*string',
    status: 'string',
  },
  InvitationOpen: {
    accepted: 'bool',
    invite_open_token: '*string',
    expires_at: '*int64',
  },
}

export interface ResponseTypes {
  initialize: Overview
  overview: Overview
  tasks: Tasks
  records: Records
  notifications: Notifications
  'notifications/seen': SeenResult
  'invitations/overview': InvitationOverview
  'invitations/attribution': InvitationAttributionResult
  'invitations/code/ensure': EnsureInvitationResult
  'invitations/redeem': RedeemResult
  'public/invitations/resolve': InvitationResolve
  'public/invitations/open': InvitationOpen
  'creator/analytics/overview': AnalyticsOverview
  'creator/analytics/contents': AnalyticsContents
}
export type Endpoint = keyof ResponseTypes
const models: Record<Endpoint, string> = {
  initialize: 'Overview',
  overview: 'Overview', tasks: 'Tasks', records: 'Records', notifications: 'Notifications',
  'notifications/seen': 'SeenResult', 'invitations/overview': 'InvitationOverview',
  'invitations/attribution': 'InvitationAttributionResult', 'invitations/code/ensure': 'EnsureInvitationResult', 'invitations/redeem': 'RedeemResult', 'public/invitations/resolve': 'InvitationResolve',
  'public/invitations/open': 'InvitationOpen', 'creator/analytics/overview': 'AnalyticsOverview',
  'creator/analytics/contents': 'AnalyticsContents',
}
export class ContractError extends Error {
  constructor(public field: string) { super(`Invalid incentives contract: ${field}`) }
}
const enums: Record<string, string[]> = {
  reward_engine: ['legacy', 'v2'],
  'Section.status': ['active', 'unconfigured', 'scheduled', 'paused', 'ended', 'ineligible', 'exhausted'],
  'Presentation.entry_state': ['active', 'completed', 'paused', 'ended', 'unavailable'],
  'QuotaPool.key': ['base_tasks', 'invite_code'],
  'Badge.reward_display_state': ['unconfigured', 'available', 'processing', 'granted', 'rejected', 'reversed', 'disabled', 'legacy_without_reward'],
  'Badge.reward_status': ['pending', 'granted', 'rejected', 'reversed'],
  'RewardRecord.status': ['pending', 'granted', 'rejected', 'reversed'],
  'RewardRecord.notification_policy': ['silent', 'contextual', 'digest'],
  'Task.status': ['not_started', 'in_progress', 'qualified', 'capped', 'ineligible', 'paused', 'ended', 'unsupported'],
  'Task.count_scope': ['per_content', 'per_identity'],
  'Task.period_scope': ['lifetime', 'utc_week', 'newcomer_window'],
  'Action.type': ['create', 'publish', 'share', 'view_content', 'view_records', 'enter_invite_code', 'share_invite_code', 'none'],
  'ProgressContent.status': ['available', 'unavailable'],
  'BalanceEntry.direction': ['credit', 'debit'],
  'RecordSummary.scope': ['matching_filter'],
  'Notification.kind': ['badge_earned', 'rewards_granted', 'rewards_adjusted'],
  'Attribution.source_kind': ['music_share', 'invite_link', 'manual_code'],
  'Redemption.state': ['not_submitted', 'processing', 'completed', 'rejected'],
  'FirstCreation.state': ['none', 'recorded', 'qualified'],
  'InvitationAttributionResult.relation_result': ['bound', 'kept_same', 'kept_other'],
  'RedeemResult.relation_result': ['bound', 'kept_same', 'kept_other', 'already_redeemed'],
  'InvitationResolve.status': ['active', 'disabled', 'unavailable'],
  'InvitationCode.status': ['active', 'disabled'],
  'MetricValue.scope': ['cumulative', 'range'],
  'MetricValue.status': ['ready', 'partial', 'unavailable'],
  'Series.status': ['ready', 'partial', 'unavailable'],
  'SeriesPoint.status': ['ready', 'partial', 'unavailable'],
}
export function isSafeHttpsUrl(value: string): boolean {
  try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password && value === value.trim() && !/[\u0000-\u0020\\]/.test(value) } catch { return false }
}
const metrics = ['public_songs', 'plays', 'likes', 'remixes']
const ranges = ['last_7_days', 'last_30_days']
const requireThat = (value: unknown, path: string) => { if (!value) throw new ContractError(path) }
function check(type: string, value: unknown, path: string): void {
  if (type.startsWith('*')) { if (value !== null) check(type.slice(1), value, path); return }
  if (type.startsWith('[]')) {
    requireThat(Array.isArray(value), path)
    requireThat((value as unknown[]).length <= 100, path)
    ;(value as unknown[]).forEach((item, index) => check(type.slice(2), item, `${path}[${index}]`))
    return
  }
  if (type === 'string') { requireThat(typeof value === 'string', path); return }
  if (type === 'bool') { requireThat(typeof value === 'boolean', path); return }
  if (type === 'int' || type === 'int64') { requireThat(Number.isSafeInteger(value) && (value as number) >= 0, path); return }
  if (type === 'SectionKey') { requireThat(['badge', 'creator_weekly', 'newcomer'].includes(value as string), path); return }
  requireThat(value !== null && typeof value === 'object' && !Array.isArray(value), path)
  const object = value as Record<string, unknown>, shape = shapes[type]
  requireThat(shape, path)
  for (const key of Object.keys(object)) requireThat(Object.hasOwn(shape, key), `${path}.${key}`)
  for (const [key, kind] of Object.entries(shape)) {
    requireThat(Object.hasOwn(object, key), `${path}.${key}`)
    check(kind, object[key], `${path}.${key}`)
    const allowed = enums[`${type}.${key}`] || enums[key]
    if (allowed && object[key] !== null) requireThat(allowed.includes(object[key] as string), `${path}.${key}`)
    if (key === 'schema_version') requireThat(object[key] === 2, `${path}.${key}`)
    if (['subject_id', 'scope_id', 'projection_revision', 'content_snapshot_id'].includes(key)) requireThat(object[key] !== '', `${path}.${key}`)
    if (key === 'timezone') requireThat(object[key] === 'UTC', `${path}.${key}`)
    if ((type === 'InvitationResolve' && key.endsWith('_url') || type === 'InvitationCode' && key === 'share_url') && object[key] !== null) requireThat(isSafeHttpsUrl(object[key] as string), `${path}.${key}`)
    if (['starts_at', 'ends_at', 'reset_at', 'server_time', 'as_of', 'created_at', 'qualified_at', 'granted_at', 'seen_at', 'occurred_at', 'earned_at'].includes(key) && object[key] !== null) requireThat(Number(object[key]) > 0, `${path}.${key}`)
  }
  semantic(type, object as any, path)
}
function semantic(type: string, v: any, path: string) {
  const ok = (condition: unknown) => requireThat(condition, path)
  if (type === 'Page') ok(v.has_more === (typeof v.next_cursor === 'string' && v.next_cursor.length > 0))
  if (type === 'Overview') {
    ok(v.sections.length === 3 && new Set(v.sections.map((s: Section) => s.key)).size === 3)
    ok(v.badges.length === 0 || v.badges.length === 8)
    ok(v.recent_rewards.length <= 10)
    for (const section of v.sections) {
      if (section.window.ends_at !== null && section.window.ends_at <= v.server_time) ok(section.status !== 'active' && !(section.presentation.actionable_remaining_points > 0))
      if (section.status === 'unconfigured') ok(section.policy_revision_id === null && section.totals.cap_points === null && section.totals.remaining_points === null && section.presentation.actionable_remaining_points === null)
      for (const pool of section.quota_pools || []) ok(pool.used_points + pool.remaining_points === pool.cap_points && (!pool.eligible || pool.ends_at > v.server_time && pool.remaining_points > 0))
    }
  }
  if (type === 'Badge') {
    if (v.reward_record) ok(v.locked_reward_points === v.reward_record.points && v.reward_receipt_id === v.reward_record.id && v.reward_status === v.reward_record.status)
    if (v.reward_display_state === 'legacy_without_reward') ok(v.reward_record === null && v.locked_reward_points === null)
  }
  if (type === 'Task') { ok(v.target > 0 && v.recent_receipts.length <= 10); if (v.reason_code === 'processing' && !v.recent_receipts.length) ok(v.status === 'qualified') }
  if (type === 'Progress') ok(v.target > 0 && v.pending_units <= v.completed_units && (v.content === null || v.content.id === v.content_id))
  if (type === 'ProgressContent' && v.status !== 'available') ok(v.title === null && v.cover_url === null)
  if (type === 'BalanceEntry') ok(v.points > 0 && (v.direction !== 'debit' || v.related_entry_id !== null))
  if (type === 'RewardRecord') {
    ok(v.granted_points <= v.points && v.reversed_points <= v.points && v.written_off_points <= v.points)
    ok(v.legacy || v.source !== null && v.balance_entries !== null)
    if (v.balance_entries !== null) ok(v.balance_entries.length <= 2)
    if (v.status === 'pending' || v.status === 'rejected') ok(v.granted_points === 0 && v.reversed_points === 0 && v.written_off_points === 0 && (v.balance_entries === null || !v.balance_entries.length))
    if (v.status === 'granted') ok(v.granted_points === v.points && v.reversed_points === 0 && v.written_off_points === 0)
    if (v.status === 'reversed') ok(v.granted_points === v.points - v.reversed_points && v.written_off_points === v.points - v.reversed_points)
    if (v.rule_id === 'welcome') ok(v.notification_policy === 'silent')
  }
  if (type === 'Notification') ok(new Set(v.receipt_ids).size === v.receipt_ids.length)
  if (type === 'MetricValue' || type === 'SeriesPoint') {
    if (v.status === 'unavailable') ok(v.value === null)
    if (v.status === 'ready') ok(v.value !== null)
  }
  if (type === 'AnalyticsRange') ok(ranges.includes(v.key) && v.end_at > v.start_at)
  if (type === 'MetricDefinition') ok(metrics.includes(v.key))
  if (type === 'Series') ok(metrics.includes(v.metric))
  if (type === 'AnalyticsOverview' || type === 'AnalyticsContents') {
    ok(['ready', 'partial', 'stale', 'unavailable'].includes(v.status))
    ok(v.supported_ranges.every((r: string) => ranges.includes(r)))
  }
  if (type === 'AnalyticsOverview') {
    ok(v.metric_definitions.length === 4 && new Set(v.metric_definitions.map((d: MetricDefinition) => d.key)).size === 4)
    ok(Object.values(v.summary).every((m: any) => m.scope === 'cumulative'))
    const points = v.series.points, length = v.range.key === 'last_7_days' ? 7 : 30
    ok(v.series.as_of === v.as_of && v.as_of <= v.server_time)
    ok(points.length === length && points[0].bucket_start === v.range.start_at && points.at(-1).bucket_end === v.range.end_at)
    points.forEach((point: SeriesPoint, i: number) => ok(point.bucket_start % 86400000 === 0 && point.bucket_end >= point.bucket_start && (i === length - 1 || point.bucket_end - point.bucket_start === 86400000) && (i === 0 || point.bucket_start === points[i - 1].bucket_end)))
  }
  if (type === 'InvitationOpen') ok(v.accepted ? v.invite_open_token !== null && v.expires_at !== null : v.invite_open_token === null && v.expires_at === null)
  if (type === 'InvitationBenefits' && !v.enabled) ok(v.welcome_points === null && v.invite_code_points === null && v.fill_window_seconds === null)
  if (type === 'InvitationResolve' && v.status !== 'active') ok(v.code === null && v.web_url === null && v.ios_open_url === null && v.ios_store_url === null)
  if (type === 'InvitationResolve' && v.status === 'active') ok(typeof v.code === 'string' && /^[A-Z0-9]{6}$/.test(v.code))
  if (type === 'InvitationCode') ok(/^[A-Z0-9]{6}$/.test(v.code))
  if (type === 'InvitationOverview') ok(!v.fill_window.eligible || v.server_time < v.fill_window.ends_at)
}
export function parseData<E extends Endpoint>(endpoint: E, data: unknown): ResponseTypes[E] {
  check(models[endpoint], data, endpoint)
  return data as ResponseTypes[E]
}
export class IncentivesApiError extends Error {
  constructor(public status: number, public errorCode: string, public details: Record<string, unknown> = {}, public retryAfter: string | null = null) { super(errorCode) }
}
/** Preserve structured API failures for page-local recovery. Never show server msg as HTML. */
export function parseResponse<E extends Endpoint>(endpoint: E, status: number, body: unknown, retryAfter: string | null = null): ResponseTypes[E] {
  requireThat(body !== null && typeof body === 'object' && !Array.isArray(body), 'response')
  const envelope = body as Record<string, unknown>
  requireThat(Number.isSafeInteger(envelope.code) && typeof envelope.msg === 'string' && Object.hasOwn(envelope, 'data'), 'response')
  if (status !== 200 || envelope.code !== 200) {
    requireThat(envelope.code === status && status >= 400 && status <= 599, 'response.code')
    const details = envelope.data
    requireThat(details !== null && typeof details === 'object' && !Array.isArray(details) && typeof (details as any).error_code === 'string', 'response.data.error_code')
    throw new IncentivesApiError(status, (details as any).error_code, details as Record<string, unknown>, retryAfter)
  }
  return parseData(endpoint, envelope.data)
}

/** Validate the negotiated extension separately without coercing the returned unknown values. */
export function parseTransitionResponse<E extends Endpoint>(endpoint: E, status: number, body: any, retryAfter: string | null = null): ResponseTypes[E] {
  if (status !== 200 || body?.code !== 200) return parseResponse(endpoint, status, body, retryAfter)
  const value = body.data
  if (value?.schema_version !== 2) throw new IncentivesApiError(426, 'unsupported_schema')
  const ok = (condition: unknown) => requireThat(condition, 'legacy_transition')
  const nullableInt = (v: unknown) => v === null || Number.isSafeInteger(v) && Number(v) >= 0
  const transition = value.transition
  ok(transition && ['not_migrated', 'migrating', 'ready', 'blocked'].includes(transition.state) && nullableInt(transition.effective_at) && (transition.reason_code === null || typeof transition.reason_code === 'string') && typeof transition.retryable === 'boolean')
  const achievement = (a: any) => {
    ok(a && typeof a.grant_id === 'string' && a.grant_id.length > 0 && ['historical_backfill', 'live'].includes(a.source) && nullableInt(a.qualified_at) && Number.isSafeInteger(a.granted_at) && a.granted_at > 0 && ['historical_no_reward', 'normal', 'non_rewarding'].includes(a.reward_disposition))
  }
  const base = structuredClone(value)
  delete base.contract_revision; delete base.transition
  if (endpoint === 'overview' || endpoint === 'initialize') {
    ok(Array.isArray(value.commemorative_badges) && value.commemorative_badges.length <= 100)
    value.commemorative_badges.forEach((b: any) => { ok(b && typeof b.key === 'string' && typeof b.name === 'string' && typeof b.icon_key === 'string' && typeof b.earned === 'boolean'); ok(b.description === undefined || typeof b.description === 'string'); ok(b.icon_url === undefined || b.icon_url === null || typeof b.icon_url === 'string' && isSafeHttpsUrl(b.icon_url)); achievement(b.achievement); ok(b.achievement.reward_disposition === 'non_rewarding') })
    delete base.commemorative_badges
    ok(Array.isArray(value.badges))
    value.badges.forEach((b: any, i: number) => {
      const e = b.progress_evidence
      ok(e && ['complete', 'partial', 'unavailable'].includes(e.coverage) && nullableInt(e.value) && nullableInt(e.since) && b.progress_count === e.value)
      ok(e.coverage === 'unavailable' ? e.value === null : e.value !== null)
      if (b.achievement !== null) achievement(b.achievement)
      const historical = b.reward_display_state === 'historical_no_reward'
      if (historical) ok(b.earned && !b.reward_enabled && b.achievement?.reward_disposition === 'historical_no_reward' && b.achievement?.source === 'historical_backfill' && b.reward_reason_code === 'historical_backfill_no_points' && b.locked_reward_points === 0 && b.reward_points === null && b.reward_record === null && b.reward_receipt_id === null && b.reward_status === null)
      if (b.achievement?.reward_disposition === 'historical_no_reward') ok(historical)
      const plain = base.badges[i]
      delete plain.progress_evidence; delete plain.achievement; delete plain.reward_reason_code
      // Only the base-schema validation copy gets a numeric placeholder; UI keeps the original null.
      if (plain.progress_count === null) plain.progress_count = 0
      if (historical) { plain.reward_display_state = 'disabled'; plain.locked_reward_points = null }
      // Unknown future badge states are rendered as unknown, never assumed to be payable.
      if (!enums['Badge.reward_display_state'].includes(plain.reward_display_state)) plain.reward_display_state = 'disabled'
    })
  }
  parseData(endpoint, base)
  return value as ResponseTypes[E]
}
